import React, { useEffect, useState } from "react";
import { collection, query, getDocs, doc, getDoc, DocumentData, updateDoc } from "firebase/firestore";
import { db } from "../firebase-config"; // Ensure your Firebase config is set up
import { Table, FormControl, InputLabel, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TextField, Button } from "@mui/material";
import enLocale from 'date-fns/locale/en-US';
import { MenuItem, Select } from "@mui/material";
import { generatePDF } from "../utils/generateBill"

interface ItemDetails {
  color: string;
  item_name: string;
  quantity: number;
  size: string;
  total_price: number;
  unit_price: number;
  variant_id: string;
}

interface Customer {
  name: string;
  contact_no_1: string;
  contact_no_2: string;
  email: string;
}

interface Address {
  address_line_1: string;
  address_line_2: string;
  city: string;
  district: string;
  postal_code: string;
}

interface OrderSummary {
  id?: string;
  customer_id: string;
  address_id: string;
  items: ItemDetails[];
  order_date: { seconds: number; nanoseconds: number }; // Firestore timestamp
  payment_method: string;
  total_price: number;
  tracking_number: string;
  customer?: Customer; // Add customer details as optional field
  address?: Address;   // Add address details as optional field
  status: string;
}

// Helper function to safely get customer or address data from Firestore
const getCustomerData = async (customerId: string): Promise<Customer | undefined> => {
  const docRef = doc(db, "customers", customerId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as Customer; // Cast to Customer type
  }
  return undefined;
};

const getAddressData = async (addressId: string): Promise<Address | undefined> => {
  const docRef = doc(db, "addresses", addressId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as Address; // Cast to Address type
  }
  return undefined;
};

const handleGenerateEbill = async (order: any) => {
  try {
    const pdf = generatePDF(order); 
    window.open(pdf.output('bloburl'), '_blank');
  } catch (error) {
    console.error("Error generating eBill:", error);
  }
};

// Create a custom theme to set font sizes for the table
const theme = createTheme({
  components: {
    // Customizing the MUI TableCell globally
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "12px", // Set the font size for all TableCell components
        },
      },
    },
  },
});

const AdminOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>("All");

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const fetchOrders = async () => {
    try {
      const ordersQuery = query(collection(db, "orderSummaries"));
      const querySnapshot = await getDocs(ordersQuery);

      const ordersData: OrderSummary[] = [];

      for (const docSnapshot of querySnapshot.docs) {
        const orderData = docSnapshot.data() as OrderSummary;

        // Include document ID
        const docId = docSnapshot.id;

        // Fetch customer and address details
        const customerData = await getCustomerData(orderData.customer_id);
        const addressData = await getAddressData(orderData.address_id);

        // Combine order, customer, and address data
        ordersData.push({
          ...orderData,
          id: docId,
          customer: customerData,
          address: addressData,
        });
      }

      // Sort orders by order_date in descending order (newest first)
      const sortedOrders = ordersData.sort((a, b) => {
        const dateA = new Date(a.order_date.seconds * 1000);
        const dateB = new Date(b.order_date.seconds * 1000);
        return dateB.getTime() - dateA.getTime(); // Sort by descending date
      });

      setOrders(sortedOrders);
      setLoading(false);
      setFilteredOrders(sortedOrders);
    } catch (error) {
      console.error("Error fetching orders: ", error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (order: OrderSummary, newStatus: string) => {
    try {
      if (!order.id) {
        console.error("Order ID is missing.");
        return;
      }

      // Update Firestore using the document ID
      const orderRef = doc(db, "orderSummaries", order.id);
      await updateDoc(orderRef, { status: newStatus });

      // Update local state
      setFilteredOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.id === order.id ? { ...o, status: newStatus } : o
        )
      );

      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.id === order.id ? { ...o, status: newStatus } : o
        )
      );

      console.log(`Status updated to ${newStatus} for order ID: ${order.id}`);
    } catch (error) {
      console.error("Error updating status: ", error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const formatDate = (timestamp: { seconds: number; nanoseconds: number }) => {
    const date = new Date(timestamp.seconds * 1000); // Convert Firestore timestamp to JS Date
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const filterOrdersByDate = () => {
    let filtered = orders;
    // Filter by date (if provided)
    if (startDate) {
      const actualEndDate = endDate || startDate;
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);

      const normalizedEndDate = new Date(actualEndDate);
      normalizedEndDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.order_date.seconds * 1000);
        return orderDate >= normalizedStartDate && orderDate <= normalizedEndDate;
      });
    }

    // Filter by status (if status is selected and not "All")
    if (statusFilter && statusFilter !== "All") {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    setFilteredOrders(filtered);
  };

  const clearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setStatusFilter("All");
    setFilteredOrders(orders);
  };

  return (
    <div style={{ padding: "10px" }}>
      <Typography variant="h4" gutterBottom>
        Admin Orders Page
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
        <div style={{ marginBottom: "20px" }}>
          <DatePicker
            label="Start Date"
            format="MM/dd/yyyy"
            value={startDate}
            views={['year', 'month', 'day']}
            onChange={(newValue) => setStartDate(newValue)}
            slots={{
              textField: (params) => <TextField {...params}
              />

            }}
          />
          <DatePicker
            label="End Date"
            format="MM/dd/yyyy"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            views={['year', 'month', 'day']}
            slots={{
              textField: (params) => <TextField {...params}
                sx={{ marginLeft: { md: "10px" } }} />
            }}
          />
          <FormControl sx={{ minWidth: 150, marginRight: "10px", marginLeft: { md: "10px" } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "12px", // Font size inside the dropdown
                    },
                  },
                },
              }}
            >
              <MenuItem value="All" sx={{ fontWeight: "bold" }}>All</MenuItem>
              <MenuItem value="Handed Over to Courier" sx={{ color: "orange", fontWeight: "bold" }}>Handed Over to Courier</MenuItem>
              <MenuItem value="In Progress" sx={{ color: "blue", fontWeight: "bold" }}>In Progress</MenuItem>
              <MenuItem value="Returned" sx={{ color: "red", fontWeight: "bold" }}>Returned</MenuItem>
              <MenuItem value="Completed" sx={{ color: "green", fontWeight: "bold" }}>Completed</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained"
            color="secondary"
            sx={{ marginLeft: "10px", marginTop: "6px" }} onClick={filterOrdersByDate}>Filter Orders</Button>
          {/* Clear Filter Button */}
          <Button
            style={{ marginLeft: "10px", marginTop: "6px" }}
            variant="contained"
            color="primary"
            onClick={clearFilter}
          >
            Clear Filter
          </Button>
        </div>
      </LocalizationProvider>

      {/* Display Orders */}
      <ThemeProvider theme={theme}>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "50px" }}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)", backgroundColor: "rgba(255, 255, 255, 0.4)" }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Customer Name</strong></TableCell>
                  <TableCell><strong>Contact No. 1</strong></TableCell>
                  <TableCell><strong>Contact No. 2</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>Items</strong></TableCell>
                  <TableCell><strong>Order Date</strong></TableCell>
                  <TableCell><strong>Payment Method</strong></TableCell>
                  <TableCell><strong>Total + Courier Charges (LKR)</strong></TableCell>
                  <TableCell><strong>Tracking Number</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Receipt</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ minWidth: "200px" }}>{order.customer?.name}</TableCell>
                    <TableCell>{order.customer?.contact_no_1}</TableCell>
                    <TableCell>{order.customer?.contact_no_2}</TableCell>
                    <TableCell>{order.customer?.email}</TableCell>
                    <TableCell sx={{ minWidth: "200px" }}>
                      {order.address?.address_line_1}, {order.address?.address_line_2}, <br />
                      {order.address?.city}, {order.address?.district}. {order.address?.postal_code}
                    </TableCell>
                    <TableCell sx={{ minWidth: "250px" }}>
                      {order.items && order.items.length > 0 ? (
                        order.items.map((item, idx) => (
                          <Typography key={idx} style={{ color: "black", fontSize: "12px" }}>
                            {item.quantity} - {item.color} color {item.item_name} ({item.size} size) <br />
                            Price: {item.unit_price} * {item.quantity} = LKR {item.total_price.toFixed(2)}
                            <br /><br />
                          </Typography>
                        ))
                      ) : (
                        <Typography>No items found</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(order.order_date)}</TableCell>
                    <TableCell>{order.payment_method}</TableCell>
                    <TableCell>{order.total_price}</TableCell>
                    <TableCell>{order.tracking_number}</TableCell>
                    <TableCell>
                      {/* Status and Dropdown */}
                      <div>
                        <Select
                          value={order.status} // Dynamically bind the value
                          onChange={(e) => handleStatusChange(order, e.target.value)} // Update Firestore and state
                          sx={{
                            minWidth: "100px", fontSize: "12px",
                            color: (() => {
                              switch (order.status) {
                                case "Handed Over to Courier":
                                  return "orange";
                                case "In Progress":
                                  return "blue";
                                case "Returned":
                                  return "red";
                                case "Completed":
                                  return "green";
                                default:
                                  return "black";
                              }
                            })(),
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontSize: "12px", // Font size inside the dropdown
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="Handed Over to Courier" sx={{ color: "orange", fontWeight: "bold" }}>Handed Over to Courier</MenuItem>
                          <MenuItem value="In Progress" sx={{ color: "blue", fontWeight: "bold" }}>In Progress</MenuItem>
                          <MenuItem value="Returned" sx={{ color: "red", fontWeight: "bold" }}>Returned</MenuItem>
                          <MenuItem value="Completed" sx={{ color: "green", fontWeight: "bold" }}>Completed</MenuItem>
                        </Select>
                      </div>
                    </TableCell>
                    <TableCell sx={{ minWidth: "110px" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleGenerateEbill(order)} // Trigger eBill generation
                        sx={{ textTransform: "none" }}
                      >
                        Generate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ThemeProvider>
    </div>
  );
};

export default AdminOrdersPage;